import "./styles.less";

import { Button, Col, Row, Skeleton, Typography } from "antd";
import React, { FC, useContext, useEffect, useRef, useState } from "react";

import { Breakpoint } from "antd/lib/_util/responsiveObserve";
import { CheckCircleTwoTone } from "@ant-design/icons";
import { ModalSimilars } from "../ModalSimilars/web";
import { PropComponentMode } from "../PropertyInterfaces";
import { TechnicalItem } from "./TechnicalItem";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import { useFacebookPixel } from "shared-components/GlobalHooks/web/FacebookPixel.hook";
import { useRouter } from "next/router";
import { useTechnicalSheet } from "./hook";
import { ConfigStateContext } from "shared-components/Contexts/Configurations/context";
import { TechnicalSheetType } from "shared-components";

interface TechnicalSheetProps {
	id: string;
	rows?: number;
	columns?: Partial<Record<Breakpoint, number>>;
	mode?: PropComponentMode;
	technicalSheets: TechnicalSheetType[];
	loading: boolean;
	isDisabledProperty?: boolean;
	operationType?: string;
	isProject?: boolean
	isMono?: boolean
	officeFriendly?: boolean
	penthouse?: boolean
	propertyType?: string | number
}

export const TechnicalSheet: FC<TechnicalSheetProps> = ({
	id,
	rows = 4,
	columns = { xxl: 2, xl: 2, lg: 2, md: 2, sm: 2, xs: 1 },
	mode,
	technicalSheets,
	loading,
	isDisabledProperty = false,
	operationType,
	isProject,
	isMono = false,
	officeFriendly = false,
	penthouse = false,
	propertyType
}) => {
	const { query } = useRouter();
	const { sendLead, formCompleted } = useTechnicalSheet({ id, utm: query });
	const FP = useFacebookPixel();
	const [activeRows, setActiveRows] = useState(rows);
	const [modalVisible, setModalVisible] = useState(false);
	const screens = useBreakpoint();
	const { country_code } = useContext(ConfigStateContext);
	const tableRef = useRef(null);

	useEffect(() => {
		if (formCompleted) {
			FP.StandardEvent("CompleteRegistration");
		}
	}, [formCompleted]);

	const breakpoint = b => {
		if (typeof b.xxl === "undefined") {
			return "xxl";
		}
		if (b.xxl) return "xxl";
		if (b.xl) return "xl";
		if (b.lg) return "lg";
		if (b.md) return "md";
		if (b.sm) return "sm";
		return "xs";
	};
	const propertyTypesWithRooms = [5, 4, 9, 19]
	const hasRooms = propertyTypesWithRooms.includes(Number(propertyType))

	if (loading) return <Skeleton active />;

	const updatedTechnicalSheets = technicalSheets?.map(item => {
		if ((item.field === "bedrooms" || item.field === "bathrooms") && item.value === "") {
			if(hasRooms){
				return {field:"rooms", text: "Ambientes", value: "0"}
			}
			else return { ...item, value: "0" };
		}
		if (item.field === "stratum" && parseInt(item.value) === 100) {
			return { ...item, value: 'Campestre' };
		}
		if (item.field === "stratum" && parseInt(item.value) === 110) {
			return { ...item, value: 'Sin Definir' };
		}
		if (country_code === "CO" && item.field === "construction_state_name" && item.value === "A estrenar") {
			return { ...item, value: 'Nuevo' };
		}
		if (country_code === "CO" && item.field === "construction_state_name" && item.value === "En Pozo") {
			return { ...item, value: 'Sobre Planos' };
		}
		return item;
	});

	if (country_code === "CO") {
		if (operationType && Number(operationType) === 2) {
			updatedTechnicalSheets.push({
				field: "documents_required",
				text: "Documentación requerida",
				value: null
			})
		} else if (operationType && Number(operationType) === 1 && !isProject) {
			updatedTechnicalSheets.push({
				field: "remodeled",
				text: "Remodelado",
				value: null
			})
		}
	}

	const hasOfficeFriendlyItem = updatedTechnicalSheets?.some(item => item.field === "office");

	if (!hasOfficeFriendlyItem && typeof officeFriendly === "boolean" && country_code !== "CO") {
		updatedTechnicalSheets.push({
			field: "office",
			text: "Apto para Oficina",
			value: officeFriendly ? "Sí" : "No"
		});
	}

	const hasPenthouseItem = updatedTechnicalSheets?.some(item => item.field === "penthouse");

	if (!hasPenthouseItem && typeof penthouse === "boolean" && Number(propertyType) === 2 && country_code !== "CO") {
		updatedTechnicalSheets?.push({
			field: "penthouse",
			text: "Penthouse",
			value: penthouse ? "Sí" : "No"
		});
	}

	const itemsToRender = updatedTechnicalSheets?.filter((_, i) => {
		if (["sm"].includes(breakpoint(screens))) {
			return i < activeRows * columns[breakpoint(screens)];
		}
		return activeRows;
	});

	const resetRows = () => {
		if (["sm", "md"].includes(breakpoint(screens))) {
			const offsetHeader = 120;
			const bodyRect = document.body.getBoundingClientRect().top;
			const tableRect = tableRef.current.getBoundingClientRect().top;
			const tablePosition = tableRect - bodyRect;
			const offsetPosition = tablePosition - offsetHeader;
			window.scrollTo(offsetPosition, 0);
		}

		setActiveRows(rows);
	};

	return (
		<>
			<ModalSimilars
				mode={mode}
				visible={modalVisible}
				onCancel={() => setModalVisible(false)}
				property_id={id}>
				<Row justify={"center"} style={{ textAlign: "center" }}>
					<Col span={24}>
						<CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: "25px" }} />
					</Col>
					<Col span={24}>
						<Typography.Text strong>
							{"¡Consulta express enviada con éxito!"}
						</Typography.Text>
					</Col>
				</Row>
			</ModalSimilars>
			<div ref={tableRef} className="technical-sheet">
				{itemsToRender?.map((o, i) => (
					<TechnicalItem
						key={`tech-sheet-item-${i}`}
						isDisabledProperty={isDisabledProperty}
						{...o}
						onAsk={sendLead}
						showSimilars={() => setModalVisible(true)}
						isMono={isMono}
					/>
				))}
			</div>
			{itemsToRender?.length < technicalSheets?.length && (
				<Button onClick={() => setActiveRows(activeRows * 3)} className="secondary">
					{"Ver más"}
				</Button>
			)}
			{itemsToRender?.length === technicalSheets?.length && rows < activeRows && (
				<Button onClick={resetRows} className="secondary">
					{"Ver menos"}
				</Button>
			)}


			<style jsx global>{`
				.technical-sheet {
					grid-template-columns: repeat(${columns[breakpoint(screens)]}, 1fr);
				}
			`}</style>
		</>
	);
};
